import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertBox,
  ConfirmDialog,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  SoftButton,
  formatSquadInfo,
  formatCurrency,
} from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../utility/errorSanitization";
import SquadSelect from "../ScoringHub/ScoringToolbar/SquadSelect";
import {
  loadBracketDesk,
  isSquadShuffled,
  setBowlerTicketCount,
  shuffleBracketSquad,
  subscribeBracketDesk,
  unshuffleBracketSquad,
  setPayoutsPaid,
} from "../../utility/brackets/api";
import {
  collectedAmount,
  owedForProduct,
  resolveContext,
  resolveTree,
} from "../../utility/brackets/resolveMatches";
import {
  productLabel,
  shuffleSummary,
  planDeal,
} from "../../utility/brackets/constants";
import { withBowlerTicketCount } from "../../utility/brackets/entryTickets";
import { getSetupUrl } from "../../utility/adminNavigation";
import SellBoard from "./SellBoard";
import TreeBoard from "./TreeBoard";
import PayBoard from "./PayBoard";
import SquadPicker from "./SquadPicker";

const viewTabClass = (active) =>
  `px-3 py-1.5 text-sm rounded-lg font-display font-bold ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20"
  }`;

const searchFieldClass =
  "w-full sm:max-w-xs px-4 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent";

const BracketDesk = ({ tournamentId, canManage, canEnterScores = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const squadParam = searchParams.get("squad") || "";
  const viewingAll = !squadParam || squadParam === "all";
  const squadId = viewingAll ? "" : squadParam;
  const productId = searchParams.get("product") || "";
  const isDisplay = searchParams.get("display") === "1";

  const [loading, setLoading] = useState(true);
  const [desk, setDesk] = useState(null);
  const [search, setSearch] = useState("");
  const [treeSearch, setTreeSearch] = useState("");
  const [phaseOverride, setPhaseOverride] = useState(null);
  const [confirmShuffle, setConfirmShuffle] = useState(false);
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [working, setWorking] = useState(false);
  const [sellDraft, setSellDraft] = useState(null);
  const loadGen = useRef(0);
  const sellFlushRef = useRef(null);

  const load = useCallback(async () => {
    if (!tournamentId) return;
    const gen = ++loadGen.current;
    try {
      const next = await loadBracketDesk(tournamentId, squadId, productId);
      if (gen !== loadGen.current) return;
      setDesk({
        ...next,
        viewSquadKey: squadId || "all",
        viewProductId: productId || next.product?.id || "",
      });
    } catch (error) {
      if (gen !== loadGen.current) return;
      notify.error(sanitizeOperationError.read(error, "brackets"));
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, [tournamentId, squadId, productId]);

  useEffect(() => {
    if (desk?.product && !productId) {
      const params = new URLSearchParams(searchParams);
      params.set("product", desk.product.id);
      if (squadId) params.set("squad", squadId);
      setSearchParams(params, { replace: true });
    }
  }, [desk?.product, productId, squadId, searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    setConfirmShuffle(false);
    setConfirmUnlock(false);
    load();
  }, [tournamentId, squadId, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!desk?.product) return undefined;
    let debounce = null;
    const scheduleLoad = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => load(), 300);
    };
    const unsubscribe = subscribeBracketDesk({
      squadId: squadId || null,
      productId: desk.product.id,
      onChange: scheduleLoad,
    });
    const poll = setInterval(() => load(), 5000);
    return () => {
      unsubscribe();
      clearInterval(poll);
      if (debounce) clearTimeout(debounce);
    };
  }, [desk?.product?.id, squadId, load]);

  const viewSquadKey = viewingAll ? "all" : squadId;
  const deskMatchesView =
    Boolean(desk) &&
    desk.viewSquadKey === viewSquadKey &&
    (!productId || !desk.viewProductId || desk.viewProductId === productId);
  const shuffled =
    deskMatchesView &&
    (Boolean(desk?.squadState?.shuffledAt) ||
      (desk?.trees || []).some((tree) => tree.squadID === squadId));
  const shuffleKnown = deskMatchesView;
  const product = desk?.product;
  const tickets = desk?.tickets || [];
  const ticketsForPlan =
    sellDraft && product
      ? withBowlerTicketCount(tickets, {
          productId: product.id,
          squadId,
          bowlerId: sellDraft.bowlerId,
          nextCount: sellDraft.count,
        })
      : tickets;
  const plan = planDeal(ticketsForPlan);

  const context = useMemo(
    () =>
      resolveContext({
        product,
        tournament: desk?.tournament,
        scores: desk?.scores,
        averages: desk?.averages,
      }),
    [product, desk]
  );

  const resolvedTrees = useMemo(() => {
    if (!product) return [];
    return (desk?.trees || []).map((tree) =>
      resolveTree(tree, tickets, desk?.matches || [], context)
    );
  }, [desk, product, tickets, context]);

  const owed = useMemo(
    () => (product ? owedForProduct(product, tickets, resolvedTrees, desk?.payouts) : []),
    [product, tickets, resolvedTrees, desk]
  );

  const defaultPhase = viewingAll || shuffled ? "trees" : "sell";
  const phase = phaseOverride || defaultPhase;

  const handleSquadChange = (nextSquad) => {
    void sellFlushRef.current?.();
    setSellDraft(null);
    const params = new URLSearchParams();
    if (nextSquad && nextSquad !== "all") params.set("squad", nextSquad);
    if (productId) params.set("product", productId);
    if (isDisplay) params.set("display", "1");
    setPhaseOverride(null);
    setTreeSearch("");
    setSearchParams(params);
  };

  const handleSellSquad = (nextSquad) => {
    void sellFlushRef.current?.();
    setSellDraft(null);
    const params = new URLSearchParams();
    if (nextSquad) params.set("squad", nextSquad);
    if (productId) params.set("product", productId);
    setPhaseOverride("sell");
    setTreeSearch("");
    setSearchParams(params);
  };

  const handleProductChange = (nextProduct) => {
    void sellFlushRef.current?.();
    setSellDraft(null);
    const params = new URLSearchParams(searchParams);
    params.set("product", nextProduct);
    setPhaseOverride(null);
    setTreeSearch("");
    setSearchParams(params);
  };

  const handleCountChange = async (bowlerId, nextCount) => {
    if (!product || !canEnterScores || !squadId) return;
    setDesk((current) => {
      if (!current) return current;
      return {
        ...current,
        tickets: withBowlerTicketCount(current.tickets, {
          productId: product.id,
          squadId,
          bowlerId,
          nextCount,
        }),
      };
    });
    try {
      await setBowlerTicketCount({
        productId: product.id,
        squadId,
        bowlerId,
        nextCount,
      });
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "tickets"));
      await load();
      throw error;
    }
  };

  const handleShuffle = async () => {
    if (!product || !canEnterScores || !squadId || !shuffleKnown || shuffled) {
      return;
    }
    setWorking(true);
    try {
      await sellFlushRef.current?.();
      if (await isSquadShuffled(product.id, squadId)) {
        setConfirmShuffle(false);
        await load();
        return;
      }
      await shuffleBracketSquad(product.id, squadId);
      setConfirmShuffle(false);
      setPhaseOverride("trees");
      notify.success("Trees are shuffled.");
      await load();
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "shuffle"));
    } finally {
      setWorking(false);
    }
  };

  const handleUnlock = async () => {
    if (!product || !canEnterScores || !squadId) return;
    setWorking(true);
    try {
      await unshuffleBracketSquad(product.id, squadId);
      setConfirmUnlock(false);
      setPhaseOverride("sell");
      notify.success("Shuffle unlocked. You can sell tickets again.");
      await load();
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "unlock"));
    } finally {
      setWorking(false);
    }
  };

  const handleMarkPaid = async (prizeRows, paid) => {
    if (!canEnterScores || !product) return;
    try {
      await setPayoutsPaid(
        (prizeRows || []).map((row) => ({
          ...row,
          productID: product.id,
          squadID: row.squadID || squadId,
        })),
        paid
      );
      await load();
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "payout"));
    }
  };

  if (loading && !desk) {
    return (
      <div className="card-board p-6 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!desk?.allProducts?.length) {
    return (
      <AlertBox type="info" title="No brackets yet">
        {canManage ? (
          <span>
            Add a bracket on{" "}
            <Link className="underline" to={getSetupUrl(tournamentId)}>
              Setup
            </Link>
            .
          </span>
        ) : (
          "Ask someone with Setup access to add a bracket."
        )}
      </AlertBox>
    );
  }

  const enabledProducts = desk.products || [];
  const leftoverCount = tickets.filter((ticket) => ticket.isLeftover).length;
  const treesOf8 = (desk.trees || []).filter((tree) => !tree.hasBye).length;
  const hasByeTree = (desk.trees || []).some((tree) => tree.hasBye);
  const hasTrees = (desk.trees || []).length > 0;
  const shuffledSquadCount = (product?.bracketProductSquad || []).filter(
    (row) => row.shuffledAt
  ).length;
  const squadCount = (desk.squads || []).length;
  const selectedSquad = (desk.squads || []).find((row) => row.id === squadId);

  const treeStatus = `${tickets.length} tickets · ${treesOf8} trees of 8${
    hasByeTree ? " · 1 tree of 7 with a bye" : ""
  }${leftoverCount ? ` · ${leftoverCount} leftover` : ""}`;
  const statusText = viewingAll
    ? `${treeStatus}${
        squadCount
          ? ` · ${shuffledSquadCount} of ${squadCount} squads shuffled`
          : ""
      }`
    : shuffled || hasTrees
      ? treeStatus
      : `${ticketsForPlan.length} tickets · ${shuffleSummary(plan)}`;

  const chrome = (
    <div className="space-y-4 print:hidden">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="sm:w-72">
          <SquadSelect
            label="Squad"
            squads={desk.squads || []}
            value={squadId || "all"}
            allLabel="All squads"
            allowAllTimes={false}
            onChange={handleSquadChange}
          />
        </div>
        {enabledProducts.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {enabledProducts.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => handleProductChange(row.id)}
                className={`px-3 py-1.5 text-sm rounded-lg font-display font-bold ${
                  product?.id === row.id
                    ? "bg-brand-charcoal text-brand-cream"
                    : "bg-white text-brand-charcoal border border-brand-charcoal/20"
                }`}
              >
                {productLabel(row)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {product ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-brand-charcoal">{statusText}</p>
          <div className="flex flex-wrap gap-2">
            {canEnterScores && !viewingAll && !shuffleKnown ? (
              <PrimaryButton type="button" disabled loading>
                Close sales and shuffle
              </PrimaryButton>
            ) : null}
            {canEnterScores && !viewingAll && shuffleKnown && !shuffled ? (
              <PrimaryButton
                type="button"
                disabled={!plan.canShuffle || working}
                onClick={async () => {
                  try {
                    await sellFlushRef.current?.();
                    setConfirmShuffle(true);
                  } catch {
                    /* already notified */
                  }
                }}
              >
                Close sales and shuffle
              </PrimaryButton>
            ) : null}
            {canEnterScores && !viewingAll && shuffleKnown && shuffled ? (
              <SoftButton type="button" onClick={() => setConfirmUnlock(true)}>
                Unlock shuffle
              </SoftButton>
            ) : null}
          </div>
        </div>
      ) : null}
      {product ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={viewTabClass(phase === "trees")}
              onClick={() => setPhaseOverride("trees")}
            >
              Brackets
            </button>
            <button
              type="button"
              className={viewTabClass(phase === "pay")}
              onClick={() => setPhaseOverride("pay")}
            >
              Prizes
            </button>
            <button
              type="button"
              className={viewTabClass(phase === "sell")}
              onClick={() => setPhaseOverride("sell")}
            >
              Sell tickets
            </button>
            {phase === "trees" && hasTrees ? (
              <button
                type="button"
                className={`${viewTabClass(false)} ml-auto`}
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("display", "1");
                  setSearchParams(params);
                }}
              >
                Full screen
              </button>
            ) : null}
          </div>
          {phase === "trees" || phase === "pay" ? (
            <input
              type="search"
              value={treeSearch}
              onChange={(event) =>
                setTreeSearch(event.target.value.substring(0, 100))
              }
              placeholder="Search name"
              aria-label="Search name"
              className={searchFieldClass}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const sellBody = viewingAll ? (
    <SquadPicker
      tournamentId={tournamentId}
      summaries={desk.squadSummaries || []}
      products={enabledProducts}
      canManage={canManage}
      onSelect={handleSellSquad}
      prompt="Which squad are you selling for?"
    />
  ) : (
    <SellBoard
      bowlers={desk.bowlers}
      tickets={tickets}
      search={search}
      onSearch={setSearch}
      onCountChange={(bowler, nextCount) =>
        handleCountChange(bowler.id, nextCount)
      }
      onDraftChange={setSellDraft}
      disabled={shuffled || !shuffleKnown}
      canSell={canEnterScores}
      flushRef={sellFlushRef}
    />
  );

  const body = !product ? null : phase === "sell" ? (
    sellBody
  ) : phase === "pay" ? (
    <PayBoard
      rows={owed}
      bowlers={desk.bowlers}
      squads={desk.squads || []}
      tournamentId={tournamentId}
      search={treeSearch}
      onMarkPaid={handleMarkPaid}
      canMarkPaid={canEnterScores}
    />
  ) : (
    <TreeBoard
      resolvedTrees={resolvedTrees}
      tickets={tickets}
      bowlers={desk.bowlers}
      squads={desk.squads || []}
      highlightQuery={treeSearch}
      emptyMessage={
        viewingAll
          ? "No trees yet. Choose a squad on Sell tickets to close sales and shuffle."
          : undefined
      }
      onSelectTicket={(ticket) => {
        const name =
          (desk.bowlers || []).find((bowler) => bowler.id === ticket.bowlerID)
            ?.name || "";
        if (!name) return;
        setTreeSearch((current) =>
          current.trim().toLowerCase() === name.toLowerCase() ? "" : name
        );
      }}
    />
  );

  if (isDisplay) {
    return (
      <div className="fixed inset-0 z-50 bg-brand-cream overflow-auto p-6">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <h1 className="heading-display text-2xl">
            {viewingAll
              ? `${productLabel(product)} · All squads`
              : `${productLabel(product)} · ${formatSquadInfo(
                  selectedSquad?.squadDate,
                  selectedSquad?.squadTime
                )}`}
          </h1>
          <SecondaryButton
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete("display");
              setSearchParams(params);
            }}
          >
            Exit full screen
          </SecondaryButton>
        </div>
        <TreeBoard
          resolvedTrees={resolvedTrees}
          tickets={tickets}
          bowlers={desk.bowlers}
          squads={desk.squads || []}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {chrome}
      <div className="card-board p-4 sm:p-6">
        {body}
        {product && tickets.length ? (
          <p className="mt-4 text-xs text-brand-muted print:hidden">
            Collected {formatCurrency(collectedAmount(product, tickets.length))}.
            {phase === "trees" && treeSearch.trim()
              ? " Showing trees for that name."
              : ""}
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={confirmShuffle}
        onClose={() => setConfirmShuffle(false)}
        onConfirm={handleShuffle}
        title="Close sales and shuffle?"
        message={shuffleSummary(plan)}
        confirmText="Shuffle"
        variant="warning"
        loading={working}
      />
      <ConfirmDialog
        isOpen={confirmUnlock}
        onClose={() => setConfirmUnlock(false)}
        onConfirm={handleUnlock}
        title="Unlock shuffle?"
        message="Trees will be cleared. You can sell tickets again."
        confirmText="Unlock"
        loading={working}
      />
    </div>
  );
};

export default BracketDesk;
